import { CopyOutlined, DeleteOutlined, LockOutlined, TeamOutlined } from '@ant-design/icons'
import { Checkbox, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import type { ContentProjectSummary } from '../../../api'
import { VistoeraActionConfirm } from '../../../ui/action'
import ProjectCoverCollage from './ProjectCoverCollage'
import { isCardNavigationKey, stopPropagationAndRun } from './visualProjectCardActions'

interface VisualProjectCardProps {
  project: ContentProjectSummary
  deleting: boolean
  copying: boolean
  selected: boolean
  selectionActive: boolean
  /** TASK-00556 — team workspace (>= 2 ACTIVE members): show the visibility
   * badge / share toggle. Personal workspaces render the original card. */
  teamEnabled?: boolean
  togglingVisibility?: boolean
  /** Owner 成员项目 management cards: read-only, no select / delete / share. */
  managementView?: boolean
  onOpen: () => void
  onCopy: () => void
  onDelete: () => void | Promise<void>
  onToggleSelect: (checked: boolean) => void
  onToggleVisibility?: () => void
}

export default function VisualProjectCard({
  project,
  deleting,
  copying,
  selected,
  selectionActive,
  teamEnabled = false,
  togglingVisibility = false,
  managementView = false,
  onOpen,
  onCopy,
  onDelete,
  onToggleSelect,
  onToggleVisibility,
}: VisualProjectCardProps) {
  const busy = deleting || copying || togglingVisibility
  const { t } = useTranslation(['contentProjects', 'actions'])

  // Server-evaluated permissions; absent (older backend) == fully permitted.
  const canEdit = project.permissions?.can_edit ?? true
  const canCopy = project.permissions?.can_copy ?? true
  const canManageVisibility = project.permissions?.can_manage_visibility ?? false
  const isShared = project.visibility === 'workspace'
  const showBadge = teamEnabled || managementView
  const creatorName = project.creator?.display_name
  const selectable = canEdit && !managementView
  const badgeLabel = isShared ? t('contentProjects:team.visibility.shared') : t('contentProjects:team.visibility.private')
  const badgeTitle =
    !project.is_mine && creatorName
      ? t('contentProjects:team.creatorLine', { name: creatorName })
      : managementView && creatorName
        ? t('contentProjects:team.creatorLine', { name: creatorName })
        : badgeLabel

  return (
    <article
      className={`cpg-card${selected ? ' cpg-card--selected' : ''}${selectionActive ? ' cpg-card--selection-mode' : ''}${canEdit ? '' : ' cpg-card--read-only'}`}
      role="button"
      tabIndex={0}
      data-testid="content-project-card"
      data-visibility={project.visibility ?? 'private'}
      data-access-mode={project.access_mode ?? 'EDITABLE'}
      onMouseDown={(e) => {
        if (e.button === 0) e.preventDefault()
      }}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (isCardNavigationKey(e.key)) {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      <div className="cpg-card-visual">
        {selectable ? (
          <div
            className={`cpg-card-select${selectionActive || selected ? ' cpg-card-select--visible' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={selected}
              aria-label={t('contentProjects:batch.selectProject', { name: project.title })}
              onChange={(e) => onToggleSelect(e.target.checked)}
            />
          </div>
        ) : null}
        <ProjectCoverCollage projectId={project.id} candidates={project.cover_candidates || []} />
        <div className="cpg-card-hover-cta">
          {canEdit ? t('contentProjects:continueEditing') : t('contentProjects:team.viewProject')}
        </div>
      </div>
      <div className="cpg-card-footer">
        <span className="cpg-card-footer-title" data-i18n-exempt="business-data" title={project.title}>{project.title}</span>
        {showBadge ? (
          <Tooltip title={badgeTitle}>
            <span
              className={`iac-share-badge cpg-share-badge${isShared ? ' is-workspace' : ''}`}
              data-testid="content-project-visibility-badge"
            >
              {isShared ? <TeamOutlined /> : <LockOutlined />}
              {!managementView && !project.is_mine && creatorName ? (
                <span className="cpg-share-badge-text" data-i18n-exempt="business-data">{creatorName}</span>
              ) : (
                <span className="cpg-share-badge-text">{badgeLabel}</span>
              )}
            </span>
          </Tooltip>
        ) : null}
        <div className="cpg-card-footer-actions nodrag" onClick={(e) => e.stopPropagation()}>
          {teamEnabled && !managementView && canManageVisibility && onToggleVisibility ? (
            <Tooltip title={isShared ? t('contentProjects:team.share.toPrivate') : t('contentProjects:team.share.toShared')}>
              <button
                type="button"
                className="cpg-card-footer-action"
                aria-label={isShared ? t('contentProjects:team.share.toPrivate') : t('contentProjects:team.share.toShared')}
                data-testid="content-project-share-toggle"
                disabled={busy}
                onClick={(e) => stopPropagationAndRun(e, onToggleVisibility)}
              >
                {isShared ? <LockOutlined /> : <TeamOutlined />}
              </button>
            </Tooltip>
          ) : null}
          {canCopy ? (
            <Tooltip title={canEdit ? t('contentProjects:actions.copy') : t('contentProjects:team.copyToMine')}>
              <button
                type="button"
                className="cpg-card-footer-action"
                aria-label={canEdit ? t('contentProjects:actions.copy') : t('contentProjects:team.copyToMine')}
                data-testid="content-project-copy"
                disabled={busy}
                onClick={(e) => stopPropagationAndRun(e, onCopy)}
              >
                <CopyOutlined />
              </button>
            </Tooltip>
          ) : null}
          {canEdit && !managementView ? (
            <VistoeraActionConfirm
              title={t('contentProjects:deleteConfirm.title')}
              description={t('contentProjects:deleteConfirm.description', { name: project.title })}
              confirmLabel={t('contentProjects:deleteConfirm.okText')}
              cancelLabel={t('actions:cancel')}
              tone="danger"
              tooltip={t('contentProjects:actions.delete')}
              disabled={busy}
              loading={deleting}
              onConfirm={onDelete}
            >
              <button
                type="button"
                className="cpg-card-footer-action cpg-card-footer-action--danger"
                aria-label={t('contentProjects:actions.delete')}
                disabled={busy}
              >
                <DeleteOutlined />
              </button>
            </VistoeraActionConfirm>
          ) : null}
        </div>
      </div>
      {managementView ? (
        <div className="cpg-card-meta" data-testid="content-project-management-meta">
          <span className="cpg-card-meta-creator" data-i18n-exempt="business-data" title={project.creator?.email}>
            {creatorName ?? t('contentProjects:team.memberProjects.unknownCreator')}
          </span>
          <span className="cpg-card-meta-time">{formatCreatedAt(project.created_at)}</span>
        </div>
      ) : null}
    </article>
  )
}

function formatCreatedAt(value: string | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}
